'use client';

import React, { useState } from 'react';
import {
  Box,
  InputAdornment,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import { Search } from 'lucide-react';

import { ErrorState } from '@/components/common/ErrorState';
import { ReferralHistorySkeleton } from '@/components/skeletons';
import { useAuth, useDebounce, useT } from '@/hooks';
import { useReferralHistory } from '@/services/query/referral.queries';
import type { ReferralUser } from '@/types/referral.types';

/**
 * ReferralHistory Component
 * Displays a paginated, searchable, and sortable table of referred users.
 * Integrates directly with the paginated backend API for high-scale performance.
 */
export const ReferralHistory: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const { t } = useT();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 500);

  const [sort, setSort] = useState('joinedAt');
  const [order, setOrder] = useState<'ASC' | 'DESC'>('DESC');

  const { data, isLoading, error, refetch } = useReferralHistory(
    {
      page: page + 1,
      limit: rowsPerPage,
      search: debouncedSearch,
      sort,
      order,
    },
    isAuthenticated,
  );

  const referrals = data?.data || [];
  const total = data?.meta?.total || 0;

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSortRequest = (property: string) => {
    const isAsc = sort === property && order === 'ASC';
    setOrder(isAsc ? 'DESC' : 'ASC');
    setSort(property);
  };

  const handleInputChange =
    (setter: React.Dispatch<React.SetStateAction<string>>) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setter(e.target.value);
    };

  const handleSearchChange = handleInputChange(setSearch);

  const handleSortByName = () => handleSortRequest('name');
  const handleSortByDate = () => handleSortRequest('joinedAt');

  const handleRefetch = () => {
    refetch();
  };

  // Pre-calculated variables for cleaner JSX
  const isNameActive = sort === 'name';
  const isDateActive = sort === 'joinedAt';
  const sortDirection = order.toLowerCase() as 'asc' | 'desc';

  const renderReferralRow = (referral: ReferralUser) => {
    const isActive = referral.status === 'active';

    // Calculate formatted date as a variable
    const date = new Date(referral.joinedAt);
    const formattedDate = isNaN(date.getTime()) ? 'N/A' : format(date, 'MMM d, yyyy');

    return (
      <TableRow
        key={referral.id}
        sx={{
          '&:hover': { bgcolor: 'action.hover' },
          transition: 'background-color 0.2s',
        }}
      >
        <TableCell sx={{ py: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                bgcolor: 'background.default',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.75rem',
                color: 'text.secondary',
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              {referral.name.substring(0, 2).toUpperCase()}
            </Box>
            <Typography variant="body2" fontWeight={500} color="text.primary">
              {referral.name}
            </Typography>
          </Box>
        </TableCell>
        <TableCell sx={{ py: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {formattedDate}
          </Typography>
        </TableCell>
        <TableCell sx={{ py: 2 }}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              px: 1.25,
              py: 0.25,
              borderRadius: 10,
              fontSize: '0.75rem',
              fontWeight: 600,
              bgcolor: isActive ? 'success.main' : 'warning.lighter',
              color: isActive ? 'success.contrastText' : 'warning.main',
              border: '1px solid',
              borderColor: isActive ? 'success.main' : 'warning.light',
              textTransform: 'capitalize',
            }}
          >
            {referral.status}
          </Box>
        </TableCell>
      </TableRow>
    );
  };

  const renderedReferralRows = referrals.map(renderReferralRow);

  let tableBodyContent;

  if (isLoading) {
    tableBodyContent = <ReferralHistorySkeleton rows={rowsPerPage} />;
  } else if (error) {
    tableBodyContent = (
      <TableRow>
        <TableCell colSpan={3} sx={{ py: 2 }}>
          <ErrorState
            compact
            title={t('messages.referral.unableToLoad')}
            message={t('messages.referral.encounteredError')}
            onRetry={handleRefetch}
          />
        </TableCell>
      </TableRow>
    );
  } else if (referrals.length === 0) {
    tableBodyContent = (
      <TableRow>
        <TableCell colSpan={3} sx={{ py: 10, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {search ? t('messages.referral.noMatchesFound') : t('messages.referral.noReferralsYet')}
          </Typography>
        </TableCell>
      </TableRow>
    );
  } else {
    tableBodyContent = renderedReferralRows;
  }

  // Mobile: each referral as its own card instead of a horizontally-scrolling table
  const renderMobileCard = (referral: ReferralUser) => {
    const isActive = referral.status === 'active';
    const date = new Date(referral.joinedAt);
    const formattedDate = isNaN(date.getTime()) ? 'N/A' : format(date, 'MMM d, yyyy');

    return (
      <Box
        key={`mobile-${referral.id}`}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 3,
          px: 2,
          py: 1.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: '50%',
              bgcolor: 'action.selected',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.75rem',
              fontWeight: 600,
              color: 'text.secondary',
              flexShrink: 0,
            }}
          >
            {referral.name.substring(0, 2).toUpperCase()}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              fontWeight={700}
              color="text.primary"
              sx={{
                fontSize: '0.9375rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {referral.name}
            </Typography>
            <Typography sx={{ fontSize: '0.8125rem' }} color="text.secondary">
              {formattedDate}
            </Typography>
          </Box>
        </Box>

        {/* Plain status dot instead of a pill+label — matches the compact
            mobile card design; desktop table keeps the text badge. */}
        <Box
          aria-label={referral.status}
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: isActive ? 'success.main' : 'warning.main',
            flexShrink: 0,
          }}
        />
      </Box>
    );
  };

  let mobileBodyContent;

  if (isLoading) {
    mobileBodyContent = Array.from({ length: Math.min(rowsPerPage, 6) }).map((_, index) => (
      <Box
        key={`mobile-skeleton-${index}`}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 3,
          px: 2,
          py: 1.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Skeleton variant="circular" width={38} height={38} />
          <Box>
            <Skeleton variant="text" width={100} height={22} />
            <Skeleton variant="text" width={70} height={18} />
          </Box>
        </Box>
        <Skeleton variant="circular" width={8} height={8} />
      </Box>
    ));
  } else if (error) {
    mobileBodyContent = (
      <ErrorState
        compact
        title={t('messages.referral.unableToLoad')}
        message={t('messages.referral.encounteredError')}
        onRetry={handleRefetch}
      />
    );
  } else if (referrals.length === 0) {
    mobileBodyContent = (
      <Typography variant="body2" color="text.secondary" sx={{ py: 6, textAlign: 'center' }}>
        {search ? t('messages.referral.noMatchesFound') : t('messages.referral.noReferralsYet')}
      </Typography>
    );
  } else {
    mobileBodyContent = referrals.map(renderMobileCard);
  }

  return (
    <Box sx={{ mt: 4 }}>
      <Box
        component={Paper}
        elevation={0}
        sx={{
          bgcolor: { xs: 'transparent', sm: 'background.paper' },
          borderWidth: { xs: 0, sm: '1px' },
          borderColor: 'divider',
          borderRadius: 4,
          overflow: 'hidden',
        }}
      >
        {/* Header Actions */}
        <Box
          sx={{
            px: { xs: 2, sm: 3 },
            py: { xs: 2, sm: 2 },
            pb: { xs: 1, sm: 2 },
            borderBottomWidth: { xs: 'none', sm: '1px' },
            borderColor: 'divider',
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            justifyContent: 'space-between',
            gap: { xs: 1.5, sm: 2 },
          }}
        >
          <Typography
            variant="subtitle2"
            fontWeight={700}
            color="text.primary"
            sx={{ fontSize: { xs: '1.125rem', sm: '0.875rem' } }}
          >
            {t('messages.referral.recentReferrals')}
          </Typography>

          <TextField
            size="small"
            placeholder={t('messages.referral.searchNameOrWallet')}
            value={search}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={16} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: { xs: '100%', sm: 260 },
              '& .MuiOutlinedInput-root': {
                borderRadius: { xs: '999px', sm: '10px' },
                bgcolor: 'action.hover',
                fontSize: { xs: '0.9375rem', sm: '0.875rem' },
                py: { xs: 0.25, sm: 0 },
                '& fieldset': { border: 'none' },
              },
            }}
          />
        </Box>

        {/* Mobile: each referral as its own card instead of a horizontally-scrolling table.
            Height-capped with its own scrollbar so a larger "rows per page" grows a
            scrollable list here instead of stretching the whole page. */}
        <Box
          sx={{
            display: { xs: 'flex', sm: 'none' },
            flexDirection: 'column',
            gap: 1.5,
            p: 2,
            maxHeight: 480,
            overflowY: 'auto',
          }}
        >
          {mobileBodyContent}
        </Box>

        {/* Scrollable Table Row Container */}
        <TableContainer
          sx={{
            display: { xs: 'none', sm: 'block' },
            overflowX: 'auto',
            maxHeight: 480,
            overflowY: 'auto',
          }}
        >
          <Table stickyHeader size="medium" sx={{ minWidth: 500 }}>
            <TableHead sx={{ '& .MuiTableCell-stickyHeader': { bgcolor: 'action.hover' } }}>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell sx={{ py: 1.5 }}>
                  <TableSortLabel
                    active={isNameActive}
                    direction={isNameActive ? sortDirection : 'asc'}
                    onClick={handleSortByName}
                    sx={{
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      color: 'text.secondary',
                      textTransform: 'uppercase',
                    }}
                  >
                    {t('messages.referral.colUser')}
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ py: 1.5 }}>
                  <TableSortLabel
                    active={isDateActive}
                    direction={isDateActive ? sortDirection : 'desc'}
                    onClick={handleSortByDate}
                    sx={{
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      color: 'text.secondary',
                      textTransform: 'uppercase',
                    }}
                  >
                    {t('messages.referral.colJoinedDate')}
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sx={{
                    py: 1.5,
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    color: 'text.secondary',
                    textTransform: 'uppercase',
                  }}
                >
                  {t('messages.referral.colStatus')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody sx={{ '& > tr:last-child td': { borderBottom: 0 } }}>
              {tableBodyContent}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination — the toolbar wraps onto its own centered lines on
            narrow screens instead of squeezing "Rows per page" + the select
            + the page range + arrows into one cramped row. */}
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage={t('referral.rowsPerPage')}
          sx={{
            borderTop: '1px solid',
            borderColor: 'divider',
            '& .MuiTablePagination-toolbar': {
              flexWrap: 'wrap',
              justifyContent: { xs: 'center', sm: 'flex-end' },
              rowGap: 0.5,
              px: { xs: 1, sm: 2 },
              minHeight: { xs: 'auto', sm: 52 },
              py: { xs: 1, sm: 0 },
            },
            '& .MuiTablePagination-spacer': {
              display: { xs: 'none', sm: 'block' },
            },
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              m: 0,
            },
            '& .MuiTablePagination-input': {
              ml: { xs: 0.5, sm: 1 },
              mr: { xs: 1.5, sm: 2.5 },
            },
          }}
        />
      </Box>
    </Box>
  );
};
